import { Element } from 'domhandler'
import serialize from 'dom-serializer'
import { trim } from 'lodash'

interface HtmlBuilderErrorContext {
    tagName?: string
    parentTagName?: string
    ancestorChain?: string[]
}

class HtmlBuilderError extends Error {
    context: HtmlBuilderErrorContext

    constructor(message: string, context: HtmlBuilderErrorContext) {
        super(message)
        this.name = 'HtmlBuilderError'
        this.context = context
    }
}

const sanitizeAttributes = (attributes: Record<string, string>): Record<string, string> => {
    const result: Record<string, string> = {}

    for (const [key, value] of Object.entries(attributes)) {
        if (key === 'class') {
            const normalized = trim(value).replace(/\s+/g, ' ')
            if (normalized) {
                result[key] = normalized
            }
            continue
        }

        if (trim(value)) {
            result[key] = value
        }
    }

    return result
}

export class HtmlBuilder {
    private root: Element
    private readonly owned = new WeakSet<Element>()

    constructor(tagName: string, attributes: Record<string, string> = {}) {
        if (!tagName) {
            throw new HtmlBuilderError('Tag name is required', { tagName })
        }
        this.root = this.createElement(tagName, attributes)
    }

    getRoot(): Element {
        return this.root
    }

    addElement(parent: Element, tagName: string, attributes: Record<string, string> = {}): Element {
        if (!tagName) {
            throw new HtmlBuilderError('Tag name is required', { tagName, parentTagName: parent.tagName })
        }

        if (!this.owned.has(parent)) {
            throw new HtmlBuilderError('Parent is not owned by this builder', {
                tagName,
                parentTagName: parent.tagName,
            })
        }

        const element = this.createElement(tagName, attributes)
        element.parent = parent
        ;(parent.children as Element[]).push(element)

        return element
    }

    private createElement(tagName: string, attributes: Record<string, string> = {}): Element {
        const sanitized = sanitizeAttributes(attributes)
        const element = new Element(tagName, sanitized, [])
        this.owned.add(element)
        return element
    }

    build(element?: Element): string {
        if (element !== undefined) {
            if (!this.owned.has(element)) {
                throw new HtmlBuilderError('Element is not owned by this builder', {
                    tagName: element.tagName,
                })
            }
            return serialize(element)
        }

        return serialize(this.root)
    }
}
